import { apiClient } from './api';
import type { TicketAttachmentSummary } from '@ventin/shared';
import type { ScreenshotResult } from '@/components/screenshot/types';

/**
 * Two-step screenshot upload (plan §1.3 / §5.5).
 *
 * The image bytes travel as a raw `image/*` body (step one), then the comment
 * and auto-captured metadata are attached as JSON (step two). This mirrors the
 * server contract: `POST /api/tickets/:id/attachments` (raw) returns the new
 * attachment id, and `PATCH …/:attachmentId` (JSON) fills comment + meta.
 *
 * We call `apiClient` directly rather than `apiFetch` because the first leg
 * needs a binary body with an `image/*` Content-Type, not JSON.
 */
export async function uploadAttachment(
  ticketId: string,
  { blob, comment, meta }: ScreenshotResult,
): Promise<TicketAttachmentSummary> {
  const created = await apiClient.request<{ attachment: TicketAttachmentSummary }>({
    url: `/api/tickets/${encodeURIComponent(ticketId)}/attachments`,
    method: 'POST',
    data: blob,
    // Browsers may not set a Blob's type on XHR; send it explicitly so the
    // route's express.raw({ type: 'image/*' }) accepts the body.
    headers: { 'Content-Type': blob.type || 'image/webp' },
  });

  const attachmentId = created.data.attachment.id;
  const patched = await apiClient.request<{ attachment: TicketAttachmentSummary }>({
    url: `/api/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentId)}`,
    method: 'PATCH',
    data: { comment: comment || undefined, meta },
  });
  return patched.data.attachment;
}
